import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import useModuleAccess from "../hooks/useModuleAccess";

const API_URL = process.env.REACT_APP_API_URL;

const SALES_QUOTE_STAT_CARDS = [
  {
    key: "totalCount",
    label: "Total Quotes",
    titleColor: "#127aea",
    valueColor: "#2c7fd8",
    filterValue: null,
    filterType: null,
  },
  {
    key: "openCount",
    label: "Open",
    titleColor: "#196F3D",
    valueColor: "#27AE60",
    filterValue: "Open",
    filterType: "quoteStatus",
  },
  {
    key: "expiredCount",
    label: "Expired",
    titleColor: "#922B21",
    valueColor: "#E74C3C",
    filterValue: "Expired",
    filterType: "quoteStatus",
  },
  {
    key: "poCreatedCount",
    label: "PO Created",
    titleColor: "#6C3483",
    valueColor: "#8E44AD",
    filterValue: "Po created",
    filterType: "quoteStatus",
  },
  {
    key: "poPartiallyCreatedCount",
    label: "PO Partially Created",
    titleColor: "#B45309",
    valueColor: "#D97706",
    filterValue: "Po Partially created",
    filterType: "quoteStatus",
  },
  {
    key: "wonCount",
    label: "Won",
    titleColor: "#145A32",
    valueColor: "#229954",
    filterValue: "Won",
    filterType: "quoteStatus",
  },
  {
    key: "holdCount",
    label: "Hold",
    titleColor: "#7D6608",
    valueColor: "#D4AC0D",
    filterValue: "Hold",
    filterType: "quoteStatus",
  },
  {
    key: "lostCount",
    label: "Lost",
    titleColor: "#641E16",
    valueColor: "#C0392B",
    filterValue: "Lost",
    filterType: "quoteStatus",
  },
  {
    key: "grossTotalBasicPrice",
    label: "Gross Basic",
    titleColor: "#1A5276",
    valueColor: "#2874A6",
    filterValue: null,
    filterType: null,
    format: "currency",
    clickable: false,
  },
];


const QUOTE_STATUS_FILTER_OPTIONS = SALES_QUOTE_STAT_CARDS.filter(
  (card) => card.filterValue,
).map((card) => ({
  value: card.filterValue,
  label: card.label,
}));

const SalesQuoteStatCard = ({
  label,
  value,
  fullValue,
  titleColor,
  valueColor,
  onClick,
  isActive,
  clickable = true,
}) => (
  <div className="sales-quote-stat-col">
    <div
      className={`card supplier-stat-card${
        clickable ? " procurement-stat-card-clickable" : ""
      }${isActive ? " procurement-stat-card-active" : ""}`}
      onClick={clickable ? onClick : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={fullValue ? fullValue : clickable ? `Filter by ${label}` : label}
      style={clickable ? undefined : { cursor: "default" }}
    >
      <div className="card-body">
        <div className="stat-title" style={{ color: titleColor }}>
          {label}
        </div>
        <div className="stat-value" style={{ color: valueColor }}>
          {value}
        </div>
      </div>
    </div>
  </div>
);

const SalesQuoteStatCardSkeleton = () => (
  <div className="sales-quote-stat-col">
    <div className="card supplier-stat-card">
      <div className="card-body">
        <div className="stat-skeleton stat-skeleton-title" />
        <div className="stat-skeleton stat-skeleton-value" />
      </div>
    </div>
  </div>
);

function parseApiResponse(raw) {
  let d = raw;
  if (typeof d === "string") {
    try {
      d = JSON.parse(d);
    } catch {
      return raw;
    }
  }
  if (d?.body != null) {
    if (typeof d.body === "string") {
      try {
        return JSON.parse(d.body);
      } catch {
        return d.body;
      }
    }
    return d.body;
  }
  return d;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };
  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }
  if (
    obj.data &&
    typeof obj.data === "object" &&
    typeof obj.data.data === "string"
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) };
  }
  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const s = responseData.success;
  return s === true || s === "true" || s === 1 || s === "1";
}

function extractQuotesPayload(responseData) {
  const root = responseData;
  let dataBlock = root?.data != null ? root.data : root;

  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") {
    return { quotes: [], pagination: {} };
  }

  if (Array.isArray(dataBlock)) {
    return {
      quotes: dataBlock,
      pagination:
        root.pagination && typeof root.pagination === "object"
          ? root.pagination
          : {},
    };
  }

  let list =
    dataBlock.quotes || dataBlock.quoteList || dataBlock.items || root.quotes;

  if (list == null && dataBlock.data != null) {
    const inner =
      typeof dataBlock.data === "string"
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data;
    if (inner && typeof inner === "object") {
      list = inner.quotes || inner.quoteList || inner.items;
    }
  }

  if (
    (list == null || (Array.isArray(list) && list.length === 0)) &&
    dataBlock.quotes == null &&
    dataBlock.quoteList == null &&
    dataBlock.items == null &&
    (dataBlock._id != null || dataBlock.quoteNumber != null)
  ) {
    list = [dataBlock];
  }

  const pagination =
    (dataBlock && dataBlock.pagination) || root.pagination || {};

  if (typeof list === "string") {
    list = parseJsonIfString(list);
  }

  const total =
    typeof pagination.total === "number"
      ? pagination.total
      : typeof pagination.totalCount === "number"
        ? pagination.totalCount
        : typeof dataBlock.totalCount === "number"
          ? dataBlock.totalCount
          : typeof dataBlock.total === "number"
            ? dataBlock.total
            : undefined;

  return {
    quotes: Array.isArray(list) ? list : [],
    pagination:
      pagination && typeof pagination === "object" && !Array.isArray(pagination)
        ? { ...pagination, ...(total != null ? { total } : {}) }
        : total != null
          ? { total }
          : {},
  };
}

function formatQuoteDate(value) {
  if (!value) return "-";
  const str = String(value).trim();
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str;
  const dateTimeMatch = str.match(/^(\d{2}-\d{2}-\d{4})[ T](\d{2}:\d{2})/);
  if (dateTimeMatch) {
    return `${dateTimeMatch[1]} ${dateTimeMatch[2]}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return str || "-";
  return d.toLocaleDateString();
}

function quoteNumber(quote) {
  return quote?.quoteNumber || quote?.quoteNo || quote?.referenceNo || "-";
}

function formatQuoteGrossBasicPrice(value) {
  if (value == null || value === "") return "-";
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getQuoteCurrencySymbol(currency) {
  const value = String(currency || "")
    .trim()
    .toLowerCase();
  if (value === "dollar" || value === "usd" || value === "$") return "$";
  if (value === "euro" || value === "eur" || value === "€") return "€";
  if (value === "inr" || value === "rs" || value === "₹") return "₹";
  return "₹";
}

/** Compact currency for equal-width stat cards; full amount stays in tooltip. */
function formatQuoteGrossBasicPriceCompact(value) {
  if (value == null || value === "") return "-";
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";

  const abs = Math.abs(num);
  if (abs >= 10000000) {
    return `₹ ${(num / 10000000).toFixed(2)} Cr`;
  }
  if (abs >= 100000) {
    return `₹ ${(num / 100000).toFixed(2)} L`;
  }
  return `₹ ${formatQuoteGrossBasicPrice(num)}`;
}

function renderQuoteGrossBasicPrice(quote) {
  const amount = formatQuoteGrossBasicPrice(quote?.grossTotalBasicPrice);
  if (amount === "-") return "-";
  const symbol = getQuoteCurrencySymbol(quote?.quoteCurrency);
  return `${symbol} ${amount}`;
}

function quoteDateValue(quote) {
  return formatQuoteDate(quote?.quoteDate);
}

function formatQuoteDateOnly(value) {
  if (!value) return "-";
  const str = String(value).trim();
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str;
  const dateOnlyMatch = str.match(/^(\d{2}-\d{2}-\d{4})/);
  if (dateOnlyMatch) return dateOnlyMatch[1];
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return str || "-";
  return d.toLocaleDateString();
}

function quoteDeliveryTimeValue(quote) {
  return formatQuoteDateOnly(quote?.quoteDeliveryTime || quote?.deliveryTime);
}

function getQuotePrNumbers(quote) {
  if (Array.isArray(quote?.prNumbers) && quote.prNumbers.length > 0) {
    const numbers = new Set();
    quote.prNumbers.forEach((prNumber) => {
      const value = String(prNumber || "").trim();
      if (value) numbers.add(value);
    });
    return [...numbers];
  }

  const materials = Array.isArray(quote?.materials) ? quote.materials : [];
  const numbers = new Set();
  materials.forEach((material) => {
    const prNumber = String(material?.prNumber || material?.prNo || "").trim();
    if (prNumber) numbers.add(prNumber);
  });
  return [...numbers];
}

function renderQuotePrNumbers(quote) {
  const prNumbers = getQuotePrNumbers(quote);
  if (prNumbers.length === 0) return "-";
  return (
    <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
      {prNumbers.join(", ")}
    </span>
  );
}

function getQuoteClientReferences(quote) {
  if (
    Array.isArray(quote?.clientReferences) &&
    quote.clientReferences.length > 0
  ) {
    const references = new Set();
    quote.clientReferences.forEach((clientReference) => {
      const value = String(clientReference || "").trim();
      if (value) references.add(value);
    });
    return [...references];
  }

  const materials = Array.isArray(quote?.materials) ? quote.materials : [];
  const references = new Set();
  materials.forEach((material) => {
    const clientReference = String(material?.clientReference || "").trim();
    if (clientReference) references.add(clientReference);
  });
  return [...references];
}

function renderQuoteClientReferences(quote) {
  const clientReferences = getQuoteClientReferences(quote);
  if (clientReferences.length === 0) return "-";
  return (
    <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
      {clientReferences.join(", ")}
    </span>
  );
}

function quoteSummaryValue(quote) {
  const s = quote?.quoteSummary || quote?.summary || "";
  return s.trim() ? s : "-";
}

function quoteCreatedByName(quote) {
  const createdBy = quote?.createdBy;
  if (!createdBy) return quote?.ownerName || "-";
  if (typeof createdBy === "string") return createdBy.trim() || "-";
  return createdBy.name || createdBy.userName || quote?.ownerName || "-";
}

function capitalizeStatus(value) {
  if (!value) return "-";
  const s = String(value).trim();
  if (!s) return "-";

  return s
    .split(",")
    .map((part) => {
      const trimmed = part.trim();
      if (!trimmed) return trimmed;
      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
    })
    .join(", ");
}

function statusBadgeClass(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "active") return "badge-success";
  if (normalized === "draft") return "badge-secondary";
  return "badge-info";
}

function renderQuoteRecordStatus(status) {
  const normalized = String(status || "").toLowerCase();
  const label = capitalizeStatus(status);

  if (normalized === "inactive") {
    return (
      <span
        className="fwt_600 f_red"
        style={{ fontSize: "90%", fontWeight: 550 }}
      >
        {label}
      </span>
    );
  }

  if (normalized === "active") {
    return (
      <span
        className="fwt_600 f_green"
        style={{ fontSize: "90%", fontWeight: 550 }}
      >
        {label}
      </span>
    );
  }

  return (
    <span
      className={`badge ${statusBadgeClass(status)}`}
      style={{ fontSize: "90%", fontWeight: 550 }}
    >
      {label}
    </span>
  );
}

function normalizeQuoteUsers(users) {
  if (!Array.isArray(users)) return [];
  return users
    .map((user) => ({
      _id: String(user._id || user.id || "").trim(),
      name: String(user.name || user.userName || user.email || "").trim(),
    }))
    .filter((user) => user._id && user.name);
}

function normalizeQuoteClientReferences(clients) {
  if (!Array.isArray(clients)) return [];
  return clients
    .map((client) => {
      const clientReferenceId = String(
        client.clientReferenceId || client._id || client.id || "",
      ).trim();
      const clientReference = String(
        client.clientReference || client.clientName || client.name || "",
      ).trim();
      return {
        clientReferenceId,
        clientReference,
        filterValue: clientReferenceId || clientReference,
      };
    })
    .filter((client) => client.clientReference);
}

function applyQuoteClientReferenceFilter(payload, filterValue) {
  const value = String(filterValue || "").trim();
  if (!value) return payload;

  if (/^[a-f\d]{24}$/i.test(value)) {
    payload.clientReferenceId = value;
  } else {
    payload.clientReference = value;
  }
  return payload;
}

const QUOTE_RECORD_STATUS_OPTIONS = ["Active", "Draft", "Inactive"];
const BULK_UPDATE_QUOTE_STATUS_OPTIONS = ["Won", "Lost", "Hold"];

function getQuoteId(quote) {
  return quote?._id || quote?.id || "";
}

const QUOTE_SORT_COLUMN_MAP = {
  quoteNumber: "quoteNumber",
  quoteDate: "quoteDate",
  quoteDeliveryTime: "quoteDeliveryTime",
  status: "status",
  quoteStatus: "quoteStatus",
  createdBy: "createdBy",
};

const DEFAULT_SORT_BY = "createdAt";
const DEFAULT_SORT_ORDER = "desc";

const DEFAULT_FILTER_CHECKS = {
  quoteStatus: false,
  status: false,
  clientReference: false,
  createdBy: false,
  dateRange: false,
};
const DEFAULT_FILTER_INPUTS = {
  quoteStatus: "",
  status: "",
  clientReference: "",
  createdBy: "",
  fromDate: "",
  toDate: "",
};
const DEFAULT_ACTIVE_MULTI_FILTERS = {
  quoteStatus: "",
  status: "",
  clientReference: "",
  createdBy: "",
  fromDate: "",
  toDate: "",
};

const MAX_ACTIVE_FILTERS = 2;

const SalesQuotes = () => {
  const { canCreate, canView, canUpdate, canDelete } =
    useModuleAccess("sales_quotes");
  const navigate = useNavigate();
  const savedFilters = JSON.parse(
    sessionStorage.getItem("salesQuotesFilters") || "{}",
  );
  const [quotes, setQuotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(
    savedFilters.searchQuery || "",
  );
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || "",
  );
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25,
  );
  const [deleteQuoteId, setDeleteQuoteId] = useState(null);
  const [deleteQuoteNumber, setDeleteQuoteNumber] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [filterChecks, setFilterChecks] = useState(
    savedFilters.filterChecks || DEFAULT_FILTER_CHECKS,
  );
  const [filterInputs, setFilterInputs] = useState(
    savedFilters.filterInputs || DEFAULT_FILTER_INPUTS,
  );
  const [activeMultiFilters, setActiveMultiFilters] = useState(
    savedFilters.activeMultiFilters || DEFAULT_ACTIVE_MULTI_FILTERS,
  );
  const filterPanelRef = useRef(null);

  const [quoteUsers, setQuoteUsers] = useState([]);
  const [isQuoteUsersLoading, setIsQuoteUsersLoading] = useState(false);
  const [sqClientReferences, setSqClientReferences] = useState([]);
  const [isSqClientReferencesLoading, setIsSqClientReferencesLoading] =
    useState(false);
  const [sortBy, setSortBy] = useState(savedFilters.sortBy || DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState(
    savedFilters.sortOrder || DEFAULT_SORT_ORDER,
  );
  const [selectedQuoteIds, setSelectedQuoteIds] = useState(() => new Set());
  const [bulkQuoteStatus, setBulkQuoteStatus] = useState("");
  const [isUpdatingBulkStatus, setIsUpdatingBulkStatus] = useState(false);
  const [isSalesQuoteStatusLoading, setIsSalesQuoteStatusLoading] =
    useState(true);

  const [showExportModal, setShowExportModal] = useState(false);
  const [exportMode, setExportMode] = useState("all");
  const [exportFromDate, setExportFromDate] = useState("");
  const [exportToDate, setExportToDate] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  const [salesQuoteStatus, setSalesQuoteStatus] = useState({
    totalCount: 0,
    openCount: 0,
    expiredCount: 0,
    wonCount: 0,
    lostCount: 0,
    holdCount: 0,
    poCreatedCount: 0,
    poPartiallyCreatedCount: 0,
    grossTotalBasicPrice: 0,
  });

  const anyMultiFilterActive =
    !!activeMultiFilters.quoteStatus ||
    !!activeMultiFilters.status ||
    !!activeMultiFilters.clientReference ||
    !!activeMultiFilters.createdBy ||
    !!activeMultiFilters.fromDate ||
    !!activeMultiFilters.toDate;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(e.target)
      ) {
        setIsFilterPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  const buildMultiFilterFields = useCallback(
    (fields = {}) => {
      if (activeMultiFilters.quoteStatus) {
        fields.quoteStatus = activeMultiFilters.quoteStatus;
      }
      if (activeMultiFilters.status) {
        fields.status = activeMultiFilters.status;
      }
      if (activeMultiFilters.clientReference) {
        applyQuoteClientReferenceFilter(
          fields,
          activeMultiFilters.clientReference,
        );
      }
      if (activeMultiFilters.createdBy) {
        fields.createdBy = activeMultiFilters.createdBy;
      }
      if (activeMultiFilters.fromDate) {
        fields.fromDate = toApiDDMMYYYY(activeMultiFilters.fromDate);
      }
      if (activeMultiFilters.toDate) {
        fields.toDate = toApiDDMMYYYY(activeMultiFilters.toDate);
      }
      return fields;
    },
    [activeMultiFilters],
  );

  function toApiDDMMYYYY(isoDate) {
    if (!isoDate) return "";
    const [yyyy, mm, dd] = isoDate.split("-");
    if (!yyyy || !mm || !dd) return "";
    return `${dd}-${mm}-${yyyy}`;
  }

  function extractExcelBase64(raw) {
    if (raw == null) return null;
    if (typeof raw === "string") {
      const t = raw.trim();
      if (t.startsWith("UEs")) return raw;
      if (t.startsWith("{")) {
        try {
          return extractExcelBase64(JSON.parse(t));
        } catch {
          return null;
        }
      }
      return null;
    }
    if (typeof raw === "object") {
      if (raw.body != null) return extractExcelBase64(raw.body);
      if (typeof raw.data === "string") return extractExcelBase64(raw.data);
      if (raw.data != null && typeof raw.data === "object") {
        const nested = extractExcelBase64(raw.data);
        if (nested) return nested;
      }
      if (raw.file != null) return extractExcelBase64(raw.file);
      if (raw.base64 != null) return extractExcelBase64(raw.base64);
    }
    return null;
  }

  function triggerXlsxDownloadFromBase64(
    base64,
    filenamePrefix = "Sales_Quotes",
  ) {
    if (!base64 || typeof base64 !== "string" || !base64.startsWith("UEs")) {
      throw new Error("Invalid Excel data received");
    }
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .slice(0, -5);
    link.download = `${filenamePrefix}-${timestamp}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  const fetchQuotes = useCallback(async () => {
    setIsLoading(true);
    try {
      const payload = {
        action: "get-all-sales-quotes",
        page: currentPage,
        limit: itemsPerPage,
      };

      const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : "";
      if (trimmedSearch) {
        payload.search = trimmedSearch;
      }

      buildMultiFilterFields(payload);

      if (sortBy) {
        payload.sortBy = sortBy;
        payload.sortOrder = sortOrder;
      }

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      const { quotes: list, pagination } = extractQuotesPayload(responseData);

      const ok =
        isApiSuccess(responseData) || (Array.isArray(list) && list.length > 0);

      if (ok) {
        setQuotes(list);
        const total =
          typeof pagination.total === "number"
            ? pagination.total
            : typeof pagination.totalCount === "number"
              ? pagination.totalCount
              : list.length;
        setTotalCount(total);
      } else {
        toast.error(responseData?.message || "Failed to fetch sales quotes", {
          autoClose: 2000,
        });
        setQuotes([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Fetch sales quotes error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      const msg =
        typeof errData === "object" && errData?.message
          ? errData.message
          : "An error occurred while fetching sales quotes";
      toast.error(msg, { autoClose: 2000 });
      setQuotes([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    activeSearchQuery,
    sortBy,
    sortOrder,
    buildMultiFilterFields,
  ]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  useEffect(() => {
    let cancelled = false;

    const fetchQuoteUsers = async () => {
      setIsQuoteUsersLoading(true);
      try {
        const response = await axios.post(API_URL, {
          action: "get-sales-quotes-users",
        });
        const responseData = normalizeApiPayload(
          parseApiResponse(response.data),
        );
        if (cancelled) return;

        if (isApiSuccess(responseData) && Array.isArray(responseData.data)) {
          setQuoteUsers(normalizeQuoteUsers(responseData.data));
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Fetch sales quotes users error:", error);
        }
      } finally {
        if (!cancelled) {
          setIsQuoteUsersLoading(false);
        }
      }
    };

    const fetchSqClientReferences = async () => {
      setIsSqClientReferencesLoading(true);
      try {
        const response = await axios.post(API_URL, {
          action: "get-sq-client-reference",
        });
        const responseData = normalizeApiPayload(
          parseApiResponse(response.data),
        );
        if (cancelled) return;

        if (isApiSuccess(responseData) && Array.isArray(responseData.data)) {
          setSqClientReferences(
            normalizeQuoteClientReferences(responseData.data),
          );
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Fetch sales quote client references error:", error);
        }
      } finally {
        if (!cancelled) {
          setIsSqClientReferencesLoading(false);
        }
      }
    };

    fetchQuoteUsers();
    fetchSqClientReferences();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!window.$) return undefined;

    const $modal = window.$("#modaldelete");
    const onHidden = () => {
      setDeleteQuoteId(null);
      setDeleteQuoteNumber("");
    };

    $modal.on("hidden.bs.modal", onHidden);
    return () => {
      $modal.off("hidden.bs.modal", onHidden);
    };
  }, []);

 const fetchSalesQuoteStatus = useCallback(async () => {
  try {
    setIsSalesQuoteStatusLoading(true);

    const payload = {
      action: "sales-quote-count",
    };

      if (activeSearchQuery?.trim()) {
      payload.search = activeSearchQuery.trim();
    }

    
    if (activeMultiFilters.quoteStatus) {
      payload.quoteStatus = activeMultiFilters.quoteStatus;
    }

    if (activeMultiFilters.status) payload.status = activeMultiFilters.status;
    if (activeMultiFilters.clientReference) {
      applyQuoteClientReferenceFilter(
        payload,
        activeMultiFilters.clientReference,
      );
    }
    if (activeMultiFilters.createdBy) {
      payload.createdBy = activeMultiFilters.createdBy;
    }
    if (activeMultiFilters.fromDate) {
      payload.fromDate = toApiDDMMYYYY(activeMultiFilters.fromDate);
    }
    if (activeMultiFilters.toDate) {
      payload.toDate = toApiDDMMYYYY(activeMultiFilters.toDate);
    }

    const response = await axios.post(API_URL, payload);

    let responseData = response.data;

    if (typeof responseData === "string") {
      responseData = JSON.parse(responseData);
    }

    if (responseData.success) {
      setSalesQuoteStatus(responseData.data);
    }
  } catch (error) {
    console.error("Sales quote status count error:", error);
  } finally {
    setIsSalesQuoteStatusLoading(false);
  }
}, [
  activeSearchQuery,
  activeMultiFilters.quoteStatus,
  activeMultiFilters.status,
  activeMultiFilters.clientReference,
  activeMultiFilters.createdBy,
  activeMultiFilters.fromDate,
  activeMultiFilters.toDate,
]);

  useEffect(() => {
    fetchSalesQuoteStatus();
  }, [fetchSalesQuoteStatus]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startItem =
    quotes.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);

      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 100);
    }
  };

  const handleCreateSalesQuoteClick = () => {
    if (window.$) {
      window.$("#modalcreatesalesquote").modal("show");
    }
  };

  const handleConfirmCreateSalesQuote = () => {
    if (window.$) {
      window.$("#modalcreatesalesquote").modal("hide");
    }
    navigate("/salesQuotesAdd");
  };

  const handleDeleteClick = (quote) => {
    const qid = quote?._id || quote?.id;
    if (!qid) {
      toast.error("Sales quote ID is missing", { autoClose: 2000 });
      return;
    }
    setDeleteQuoteId(qid);
    setDeleteQuoteNumber(quoteNumber(quote));
  };

  const handleDelete = async () => {
    if (!deleteQuoteId) return;

    const requestUserId = localStorage.getItem("id");
    if (!requestUserId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      return;
    }

    setIsDeleting(true);
    try {
      const response = await axios.post(API_URL, {
        action: "delete-sales-quote",
        id: deleteQuoteId,
        requestUserId,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        toast.success(
          responseData?.message || "Sales quote deleted successfully",
          { autoClose: 2000 },
        );
        if (window.$) {
          window.$("#modaldelete").modal("hide");
        }
        setDeleteQuoteId(null);
        setDeleteQuoteNumber("");
        if (quotes.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchQuotes();
        }
      } else {
        toast.error(responseData?.message || "Failed to delete sales quote", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Delete sales quote error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        errData?.message || "An error occurred while deleting sales quote",
        {
          autoClose: 2000,
        },
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExportSalesQuotes = async () => {
    if (exportMode === "date") {
      if (!exportFromDate || !exportToDate) {
        toast.error("Please select both from date and to date", {
          autoClose: 2000,
        });
        return;
      }
      if (new Date(exportFromDate) > new Date(exportToDate)) {
        toast.error("From date cannot be greater than to date", {
          autoClose: 2000,
        });
        return;
      }
    }

    setIsExporting(true);

    try {
      const payload = {
        action: "export-sales-quotes",
      };

      if (activeSearchQuery?.trim()) {
        payload.search = activeSearchQuery.trim();
      }

      buildMultiFilterFields(payload);

      if (exportMode === "date") {
        payload.fromDate = toApiDDMMYYYY(exportFromDate);
        payload.toDate = toApiDDMMYYYY(exportToDate);
      }

      const response = await axios.post(API_URL, payload, {
        responseType: "json",
      });

      const base64 = extractExcelBase64(response.data);
      if (!base64) {
        throw new Error("Invalid Excel data received from server");
      }

      triggerXlsxDownloadFromBase64(base64, "Sales_Quotes");

      toast.success("Sales Quotes exported successfully.", {
        autoClose: 2000,
      });
      setShowExportModal(false);
    } catch (error) {
      console.error(error);
      toast.error("Failed to export Sales Quotes.", {
        autoClose: 2000,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;
    const tp = totalPages;

    if (tp <= maxVisiblePages) {
      for (let i = 1; i <= tp; i++) pages.push(i);
    } else if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push("ellipsis");
      pages.push(tp);
    } else if (currentPage >= tp - 3) {
      pages.push(1);
      pages.push("ellipsis");
      for (let i = tp - 4; i <= tp; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("ellipsis");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push("ellipsis");
      pages.push(tp);
    }
    return pages;
  };

  useEffect(() => {
    const filters = {
      searchQuery,
      activeSearchQuery,
      currentPage,
      itemsPerPage,
      filterChecks,
      filterInputs,
      activeMultiFilters,
      sortBy,
      sortOrder,
    };

    sessionStorage.setItem("salesQuotesFilters", JSON.stringify(filters));
  }, [
    searchQuery,
    activeSearchQuery,
    currentPage,
    itemsPerPage,
    filterChecks,
    filterInputs,
    activeMultiFilters,
    sortBy,
    sortOrder,
  ]);

  const hasActiveFilters = Boolean(activeSearchQuery || anyMultiFilterActive);

  const handleFilterCheckToggle = (key) => {
    setFilterChecks((prev) => {
      const isCurrentlyChecked = prev[key];

      if (!isCurrentlyChecked) {
        const activeCount = Object.values(prev).filter(Boolean).length;
        if (activeCount >= MAX_ACTIVE_FILTERS) {
          toast.error(
            `You can select up to ${MAX_ACTIVE_FILTERS} filters only`,
            {
              autoClose: 2000,
            },
          );
          return prev;
        }
      }

      const next = { ...prev, [key]: !isCurrentlyChecked };

      if (!next[key]) {
        if (key === "dateRange") {
          setFilterInputs((inputs) => ({
            ...inputs,
            fromDate: "",
            toDate: "",
          }));
          setActiveMultiFilters((active) => ({
            ...active,
            fromDate: "",
            toDate: "",
          }));
        } else {
          setFilterInputs((inputs) => ({ ...inputs, [key]: "" }));
          setActiveMultiFilters((active) => ({ ...active, [key]: "" }));
        }
        if (currentPage !== 1) {
          setCurrentPage(1);
        }
      }

      return next;
    });
  };

  const handleFilterInputChange = (key, value) => {
    if (key === "fromDate" || key === "toDate") {
      const nextFrom = key === "fromDate" ? value : filterInputs.fromDate;
      const nextTo = key === "toDate" ? value : filterInputs.toDate;

      if (nextFrom && nextTo && new Date(nextFrom) > new Date(nextTo)) {
        toast.error("From date cannot be greater than to date", {
          autoClose: 2000,
        });
        return;
      }

      setFilterInputs((prev) => ({ ...prev, [key]: value }));
      setActiveMultiFilters((prev) => ({
        ...prev,
        fromDate: nextFrom,
        toDate: nextTo,
      }));
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      return;
    }

    setFilterInputs((prev) => ({ ...prev, [key]: value }));
    setActiveMultiFilters((prev) => ({ ...prev, [key]: value }));
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleClearFilter = () => {
    setFilterChecks(DEFAULT_FILTER_CHECKS);
    setFilterInputs(DEFAULT_FILTER_INPUTS);
    setActiveMultiFilters(DEFAULT_ACTIVE_MULTI_FILTERS);
    setIsFilterPanelOpen(false);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleClearAll = () => {
    setSearchQuery("");
    setActiveSearchQuery("");
    handleClearFilter();
  };

  const toggleQuoteSelection = (quoteId) => {
    if (!quoteId) return;
    setSelectedQuoteIds((prev) => {
      const next = new Set(prev);
      if (next.has(quoteId)) {
        next.delete(quoteId);
      } else {
        next.add(quoteId);
      }
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    const pageIds = quotes.map(getQuoteId).filter(Boolean);
    if (pageIds.length === 0) return;

    const allSelected = pageIds.every((id) => selectedQuoteIds.has(id));
    setSelectedQuoteIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const isAllOnPageSelected =
    quotes.length > 0 &&
    quotes.every((quote) => {
      const id = getQuoteId(quote);
      return id && selectedQuoteIds.has(id);
    });

  const handleBulkStatusSubmit = async () => {
    const ids = [...selectedQuoteIds];
    if (ids.length === 0) {
      toast.error("Select at least one sales quote", { autoClose: 2000 });
      return;
    }
    if (!bulkQuoteStatus) {
      toast.error("Select a quote status", { autoClose: 2000 });
      return;
    }

    const userid = localStorage.getItem("id");
    if (!userid) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      return;
    }

    setIsUpdatingBulkStatus(true);
    try {
      const response = await axios.post(API_URL, {
        action: "update-sales-quote-status",
        ids,
        quoteStatus: bulkQuoteStatus,
        userid,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        toast.success(
          responseData?.message || "Quote status updated successfully",
          {
            autoClose: 2000,
          },
        );
        setSelectedQuoteIds(new Set());
        setBulkQuoteStatus("");
        fetchQuotes();
      } else {
        toast.error(responseData?.message || "Failed to update quote status", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Bulk update sales quote status error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        errData?.message || "An error occurred while updating quote status",
        {
          autoClose: 2000,
        },
      );
    } finally {
      setIsUpdatingBulkStatus(false);
    }
  };

  // Status filter driven by the stat cards at the top - integrates into the
  // same checkbox multi-filter set as "quoteStatus"
  const isSalesQuoteStatCardActive = (card) => {
    if (card.clickable === false) return false;
    if (!card.filterValue) {
      return !activeMultiFilters.quoteStatus;
    }
    return activeMultiFilters.quoteStatus === card.filterValue;
  };

  const handleSalesQuoteStatCardClick = (card) => {
    if (card.clickable === false) return;

    if (!card.filterValue) {
      setFilterChecks((prev) => ({ ...prev, quoteStatus: false }));
      setFilterInputs((prev) => ({ ...prev, quoteStatus: "" }));
      setActiveMultiFilters((prev) => ({ ...prev, quoteStatus: "" }));
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      return;
    }

    if (!filterChecks.quoteStatus) {
      const otherActiveKeys = Object.keys(filterChecks).filter(
        (key) => key !== "quoteStatus" && filterChecks[key],
      );

      if (otherActiveKeys.length >= MAX_ACTIVE_FILTERS) {
        const keyToRemove = otherActiveKeys[0];
        const clearRemovedInputs =
          keyToRemove === "dateRange"
            ? { fromDate: "", toDate: "" }
            : { [keyToRemove]: "" };
        setFilterChecks((prev) => ({
          ...prev,
          [keyToRemove]: false,
          quoteStatus: true,
        }));
        setFilterInputs((prev) => ({
          ...prev,
          ...clearRemovedInputs,
          quoteStatus: card.filterValue,
        }));
        setActiveMultiFilters((prev) => ({
          ...prev,
          ...clearRemovedInputs,
          quoteStatus: card.filterValue,
        }));
        toast.info("Replaced an existing filter to apply Quote Status", {
          autoClose: 2000,
        });
      } else {
        setFilterChecks((prev) => ({ ...prev, quoteStatus: true }));
        setFilterInputs((prev) => ({ ...prev, quoteStatus: card.filterValue }));
        setActiveMultiFilters((prev) => ({
          ...prev,
          quoteStatus: card.filterValue,
        }));
      }
    } else {
      setFilterInputs((prev) => ({ ...prev, quoteStatus: card.filterValue }));
      setActiveMultiFilters((prev) => ({
        ...prev,
        quoteStatus: card.filterValue,
      }));
    }

    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const formatSalesQuoteStatCardValue = (card, rawValue) => {
    if (card.format === "currency") {
      const compact = formatQuoteGrossBasicPriceCompact(rawValue);
      const full =
        formatQuoteGrossBasicPrice(rawValue) === "-"
          ? "₹ 0.00"
          : `₹ ${formatQuoteGrossBasicPrice(rawValue)}`;
      return { display: compact === "-" ? "₹ 0.00" : compact, full };
    }
    return { display: rawValue ?? 0, full: null };
  };

  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    const trimmedValue = newValue ? newValue.trim() : "";
    if (trimmedValue === "" && activeSearchQuery !== "") {
      setActiveSearchQuery("");
      if (currentPage !== 1) setCurrentPage(1);
    }
  };

  const handleSearchTrigger = () => {
    const trimmedQuery = searchQuery ? searchQuery.trim() : "";
    if (trimmedQuery === "") {
      setActiveSearchQuery("");
      if (currentPage !== 1) setCurrentPage(1);
    } else {
      setActiveSearchQuery(trimmedQuery);
      if (currentPage !== 1) setCurrentPage(1);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchTrigger();
    }
  };

  const handleSort = (column) => {
    const apiField = QUOTE_SORT_COLUMN_MAP[column] || column;

    if (sortBy === apiField) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortBy(DEFAULT_SORT_BY);
        setSortOrder(DEFAULT_SORT_ORDER);
      }
    } else {
      setSortBy(apiField);
      setSortOrder("asc");
    }

    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const getSortIcon = (column) => {
    const apiField = QUOTE_SORT_COLUMN_MAP[column] || column;

    if (sortBy !== apiField) {
      return (
        <i
          className="fa-solid fa-sort text-muted ml-1"
          style={{ fontSize: "0.8em", flexShrink: 0 }}
        />
      );
    }

    if (sortOrder === "asc") {
      return (
        <i
          className="fa-solid fa-sort-up text-primary ml-1"
          style={{ fontSize: "0.8em", flexShrink: 0 }}
        />
      );
    }

    return (
      <i
        className="fa-solid fa-sort-down text-primary ml-1"
        style={{ fontSize: "0.8em", flexShrink: 0 }}
      />
    );
  };

  const renderSortableHeader = (column, label, extraStyle = {}) => (
    <th
      onClick={() => handleSort(column)}
      style={{
        cursor: "pointer",
        whiteSpace: "nowrap",
        ...extraStyle,
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {label}
        {getSortIcon(column)}
      </span>
    </th>
  );

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Sales Quotes</h1>
            </div>
            <div className="col-sm-6 txt_rgt">
              {canCreate && (
                <button
                  type="button"
                  className="btn btn-blue"
                  onClick={handleCreateSalesQuoteClick}
                >
                  Create Sales Quote
                </button>
              )}
              <button
                type="button"
                className="btn btn-success btn-sm mr-2 ml-2 mb-1"
                onClick={() => setShowExportModal(true)}
                disabled={isExporting}
              >
                <i
                  className={`fa-solid ${isExporting ? "fa-spinner fa-spin" : "fa-file-export"} mr-1`}
                />
                {isExporting ? "Exporting..." : "Export"}
              </button>

              <button
                type="button"
                className="btn btn-blue btn-sm mr-2 mb-1"
                onClick={handleBulkStatusSubmit}
                disabled={
                  isUpdatingBulkStatus ||
                  selectedQuoteIds.size === 0 ||
                  !bulkQuoteStatus
                }
              >
                {isUpdatingBulkStatus ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="sales-quote-stat-card-row">
                {isSalesQuoteStatusLoading
                  ? SALES_QUOTE_STAT_CARDS.map((card) => (
                      <SalesQuoteStatCardSkeleton key={card.key} />
                    ))
                  : SALES_QUOTE_STAT_CARDS.map((card) => {
                      const formatted = formatSalesQuoteStatCardValue(
                        card,
                        salesQuoteStatus[card.key],
                      );
                      return (
                        <SalesQuoteStatCard
                          key={card.key}
                          label={card.label}
                          value={formatted.display}
                          fullValue={formatted.full}
                          titleColor={card.titleColor}
                          valueColor={card.valueColor}
                          isActive={isSalesQuoteStatCardActive(card)}
                          clickable={card.clickable !== false}
                          onClick={() => handleSalesQuoteStatCardClick(card)}
                        />
                      );
                    })}
              </div>
              <div>
                <div className="row align-items-end mb-4 flex-xl-nowrap">
                  <div className="col-12 col-sm-6 col-md-4 col-xl-3 mb-2 mb-xl-0">
                    <div className="form-group mb-0">
                      <label
                        className="mb-1"
                        style={{ fontSize: "13px", fontWeight: "500" }}
                      >
                        Search
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search sales quotes..."
                          value={searchQuery}
                          onChange={handleSearchChange}
                          onKeyPress={handleSearchKeyPress}
                        />
                        <div className="input-group-append">
                          <div
                            className="input-group-text crsr_pntr"
                            onClick={handleSearchTrigger}
                            style={{ cursor: "pointer" }}
                          >
                            <i className="fa-solid fa-search" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                    <div
                      className="form-group mb-0 position-relative"
                      ref={filterPanelRef}
                    >
                      <label
                        className="mb-1"
                        style={{ fontSize: "13px", fontWeight: "500" }}
                      >
                        Filter
                      </label>
                      <button
                        type="button"
                        className="form-control text-left d-flex justify-content-between align-items-center"
                        onClick={() => setIsFilterPanelOpen((open) => !open)}
                      >
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {anyMultiFilterActive ? "Filters" : "Select Filters"}
                        </span>
                        <i
                          className={`fa-solid fa-chevron-${isFilterPanelOpen ? "up" : "down"}`}
                          style={{
                            fontSize: "11px",
                            flexShrink: 0,
                            marginLeft: "6px",
                          }}
                        />
                      </button>

                      {isFilterPanelOpen && (
                        <div
                          className="procurement-filter-dropdown"
                          style={{
                            position: "absolute",
                            zIndex: 20,
                            top: "100%",
                            left: 0,
                            marginTop: "4px",
                            background: "#fff",
                            border: "1px solid #ced4da",
                            borderRadius: "4px",
                            padding: "10px 12px",
                            width: "200px",
                            maxWidth: "calc(100vw - 32px)",
                            boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                          }}
                        >
                          {[
                            {
                              key: "quoteStatus",
                              id: "filterCheckQuoteStatus",
                              label: "Quote Status",
                            },
                            {
                              key: "status",
                              id: "filterCheckStatus",
                              label: "Status",
                            },
                            {
                              key: "clientReference",
                              id: "filterCheckClient",
                              label: "Client",
                            },
                            {
                              key: "createdBy",
                              id: "filterCheckCreatedBy",
                              label: "Created User",
                            },
                            {
                              key: "dateRange",
                              id: "filterCheckDateRange",
                              label: "Date",
                            },
                          ].map((filter) => {
                            const activeCount =
                              Object.values(filterChecks).filter(
                                Boolean,
                              ).length;
                            return (
                              <div
                                className="custom-control custom-checkbox mb-2"
                                key={filter.key}
                              >
                                <input
                                  type="checkbox"
                                  className="custom-control-input"
                                  id={filter.id}
                                  checked={filterChecks[filter.key]}
                                  disabled={
                                    !filterChecks[filter.key] &&
                                    activeCount >= MAX_ACTIVE_FILTERS
                                  }
                                  onChange={() =>
                                    handleFilterCheckToggle(filter.key)
                                  }
                                />
                                <label
                                  className="custom-control-label"
                                  htmlFor={filter.id}
                                >
                                  {filter.label}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {filterChecks.quoteStatus && (
                    <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Quote Status
                        </label>
                        <select
                          className="form-control"
                          value={filterInputs.quoteStatus}
                          onChange={(e) =>
                            handleFilterInputChange(
                              "quoteStatus",
                              e.target.value,
                            )
                          }
                        >
                          <option value="">Select Quote Status</option>
                          {QUOTE_STATUS_FILTER_OPTIONS.map((status) => (
                            <option key={status.value} value={status.value}>
                              {status.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {filterChecks.status && (
                    <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Status
                        </label>
                        <select
                          className="form-control"
                          value={filterInputs.status}
                          onChange={(e) =>
                            handleFilterInputChange("status", e.target.value)
                          }
                        >
                          <option value="">Select Status</option>
                          {QUOTE_RECORD_STATUS_OPTIONS.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {filterChecks.clientReference && (
                    <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Client
                        </label>
                        <select
                          className="form-control"
                          value={filterInputs.clientReference}
                          onChange={(e) =>
                            handleFilterInputChange(
                              "clientReference",
                              e.target.value,
                            )
                          }
                        >
                          <option value="">
                            {isSqClientReferencesLoading &&
                            sqClientReferences.length === 0
                              ? "Loading clients..."
                              : "Select Client"}
                          </option>
                          {sqClientReferences.map((client, index) => (
                            <option
                              key={`${client.filterValue}-${index}`}
                              value={client.filterValue}
                            >
                              {client.clientReference}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {filterChecks.createdBy && (
                    <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Created User
                        </label>
                        <select
                          className="form-control"
                          value={filterInputs.createdBy}
                          onChange={(e) =>
                            handleFilterInputChange("createdBy", e.target.value)
                          }
                        >
                          <option value="">
                            {isQuoteUsersLoading && quoteUsers.length === 0
                              ? "Loading users..."
                              : "Select User"}
                          </option>
                          {quoteUsers.map((user) => (
                            <option key={user._id} value={user._id}>
                              {user.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {filterChecks.dateRange && (
                    <>
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{ fontSize: "13px", fontWeight: "500" }}
                          >
                            From Date
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            value={filterInputs.fromDate || ""}
                            max={filterInputs.toDate || undefined}
                            onChange={(e) =>
                              handleFilterInputChange(
                                "fromDate",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{ fontSize: "13px", fontWeight: "500" }}
                          >
                            To Date
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            value={filterInputs.toDate || ""}
                            min={filterInputs.fromDate || undefined}
                            onChange={(e) =>
                              handleFilterInputChange("toDate", e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                    <div className="form-group mb-0">
                      <label
                        className="mb-1"
                        style={{ fontSize: "13px", fontWeight: "500" }}
                      >
                        Set Quote Status
                      </label>
                      <select
                        className="form-control"
                        value={bulkQuoteStatus}
                        onChange={(e) => setBulkQuoteStatus(e.target.value)}
                        disabled={isUpdatingBulkStatus}
                      >
                        <option value="">Select Status</option>
                        {BULK_UPDATE_QUOTE_STATUS_OPTIONS.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {anyMultiFilterActive && (
                    <div className="col-12 col-sm-6 col-md-4 col-xl-auto mb-2 mb-xl-0">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm w-100"
                        onClick={handleClearFilter}
                      >
                        <i className="fa-solid fa-times mr-1" />
                        Clear
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {isLoading ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : quotes.length === 0 && !hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  There are no sales quotes yet.
                  <br />
                  <div className="mt-3">
                    {canCreate && (
                      <button
                        type="button"
                        className="btn btn-blue"
                        onClick={handleCreateSalesQuoteClick}
                      >
                        Create Sales Quote
                      </button>
                    )}
                  </div>
                </div>
              ) : quotes.length === 0 && hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  No sales quotes found matching your filters.
                  <br />
                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-blue"
                      onClick={handleClearAll}
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              ) : (
                <div className="supply-table-container">
                  <div className="table-responsive">
                    <table
                      id="salesQuotes"
                      width="100%"
                      className="table table-striped table-bordered txt_lft"
                    >
                      <thead>
                        <tr>
                          <th
                            style={{
                              whiteSpace: "nowrap",
                              width: "40px",
                              minWidth: "40px",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isAllOnPageSelected}
                              onChange={toggleSelectAllOnPage}
                              disabled={
                                quotes.length === 0 || isUpdatingBulkStatus
                              }
                              aria-label="Select all quotes on this page"
                            />
                          </th>
                          <th
                            style={{
                              whiteSpace: "nowrap",
                              width: "35px",
                              minWidth: "35px",
                            }}
                          >
                            S.No
                          </th>
                          {renderSortableHeader("quoteNumber", "Quote No", {
                            minWidth: "100px",
                          })}
                          {renderSortableHeader("quoteDate", "Quote Date", {
                            minWidth: "100px",
                          })}
                          {renderSortableHeader(
                            "quoteDeliveryTime",
                            "Delivery Time",
                            { minWidth: "100px" },
                          )}
                          <th style={{ minWidth: "100px" }}>PR Number</th>
                          <th style={{ minWidth: "100px" }}>Client</th>
                          <th style={{ minWidth: "170px" }}>Summary</th>
                          {renderSortableHeader("status", "Status", {
                            minWidth: "75px",
                          })}
                          {renderSortableHeader("quoteStatus", "Quote Status", {
                            minWidth: "85px",
                          })}
                          <th style={{ minWidth: "93px" }}>Basic Price</th>
                          {renderSortableHeader("createdBy", "Created User", {
                            minWidth: "130px",
                          })}
                          <th style={{ width: "80px", minWidth: "80px" }}>
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {quotes.map((quote, index) => {
                          const qid = getQuoteId(quote);
                          const isSelected = Boolean(
                            qid && selectedQuoteIds.has(qid),
                          );
                          return (
                            <tr key={qid || index}>
                              <td className="text-center">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleQuoteSelection(qid)}
                                  disabled={!qid || isUpdatingBulkStatus}
                                  aria-label={`Select quote ${quoteNumber(quote)}`}
                                />
                              </td>
                              <td>
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {quoteNumber(quote)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {quoteDateValue(quote)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {quoteDeliveryTimeValue(quote)}
                              </td>
                              <td
                                style={{
                                  maxWidth: "140px",
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {renderQuotePrNumbers(quote)}
                              </td>
                              <td
                                style={{
                                  maxWidth: "140px",
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {renderQuoteClientReferences(quote)}
                              </td>
                              <td
                                style={{
                                  maxWidth: "240px",
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {quoteSummaryValue(quote)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {renderQuoteRecordStatus(quote.status)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {capitalizeStatus(quote?.quoteStatus)}
                              </td>
                              <td
                                style={{
                                  whiteSpace: "nowrap",
                                  fontSize: "0.85rem",
                                }}
                              >
                                {renderQuoteGrossBasicPrice(quote)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {quoteCreatedByName(quote)}
                              </td>
                              <td>
                                <div
                                  align="center"
                                  style={{ whiteSpace: "nowrap" }}
                                  className="d-flex justify-content-center align-items-center"
                                >
                                  {canView && (
                                    <Link
                                      to={`/salesQuotesView/${qid}`}
                                      className="crsr_pntr mr-2"
                                      title="View"
                                    >
                                      <i className="fa-solid fa-eye" />
                                    </Link>
                                  )}
                                  {canUpdate && (
                                    <Link
                                      to={`/salesQuotesEdit/${qid}`}
                                      className="crsr_pntr mr-2"
                                      title="Edit"
                                    >
                                      <i className="fa-solid fa-pen" />
                                    </Link>
                                  )}
                                  {canDelete && (
                                    <a
                                      href="#modaldelete"
                                      className="crsr_pntr action-icon-link"
                                      data-toggle="modal"
                                      data-target="#modaldelete"
                                      title="Delete"
                                      onClick={() => handleDeleteClick(quote)}
                                    >
                                      <i className="fa-solid fa-trash-can" />
                                    </a>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {!isLoading && quotes.length > 0 && (
                    <div className="supply-pagination-container">
                      <div className="pagination-wrapper">
                        {/* Entries Section */}
                        <div className="pagination-info">
                          <label
                            className="mb-0 mr-2"
                            style={{ fontSize: "13px" }}
                          >
                            Show:
                          </label>

                          <select
                            className="form-control form-control-sm"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>

                          <span className="ml-2" style={{ fontSize: "13px" }}>
                            entries
                          </span>

                          <span
                            className="ml-3 text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            {startItem}–{endItem} of {totalCount}
                          </span>
                        </div>

                        {/* Pagination Buttons */}
                        <nav className="pagination-nav">
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                title="First Page"
                              >
                                <i className="fa-solid fa-angle-double-left"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={currentPage === 1}
                                title="Previous Page"
                              >
                                <i className="fa-solid fa-angle-left"></i>
                              </button>
                            </li>

                            {getPageNumbers().map((page, index) =>
                              page === "ellipsis" ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="page-item disabled"
                                >
                                  <span className="page-link">...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              ),
                            )}

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                                disabled={currentPage === totalPages}
                                title="Next Page"
                              >
                                <i className="fa-solid fa-angle-right"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                title="Last Page"
                              >
                                <i className="fa-solid fa-angle-double-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modalcreatesalesquote"
        tabIndex={-1}
        aria-labelledby="modalcreatesalesquoteLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalcreatesalesquoteLabel">
                Create Sales Quote
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to create Sales Quote?
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
              >
                No
              </button>
              <button
                type="button"
                className="btn btn-blue"
                onClick={handleConfirmCreateSalesQuote}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modaldelete"
        tabIndex={-1}
        aria-labelledby="modaldeleteLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modaldeleteLabel">
                Delete Sales Quote
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                disabled={isDeleting}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to delete
                <br />
                <span className="fwt_600">{deleteQuoteNumber || "-"}</span>?
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`modal fade ${showExportModal ? "show" : ""}`}
        style={{ display: showExportModal ? "block" : "none" }}
        tabIndex={-1}
        aria-labelledby="modalExportSalesQuoteLabel"
        aria-hidden={!showExportModal}
        onClick={() => {
          if (!isExporting) setShowExportModal(false);
        }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalExportSalesQuoteLabel">
                Export Sales Quotes
              </h5>
              <button
                type="button"
                className="close"
                aria-label="Close"
                onClick={() => {
                  if (!isExporting) setShowExportModal(false);
                }}
                disabled={isExporting}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              {hasActiveFilters && (
                <p className="text-muted small mb-3">
                  Current filters will be included:
                  {activeSearchQuery && (
                    <span className="d-block">Search: {activeSearchQuery}</span>
                  )}
                  {activeMultiFilters.quoteStatus && (
                    <span className="d-block">
                      Quote Status: {activeMultiFilters.quoteStatus}
                    </span>
                  )}
                  {activeMultiFilters.status && (
                    <span className="d-block">
                      Status: {activeMultiFilters.status}
                    </span>
                  )}
                  {activeMultiFilters.clientReference && (
                    <span className="d-block">Client filter selected</span>
                  )}
                  {activeMultiFilters.createdBy && (
                    <span className="d-block">Created User selected</span>
                  )}
                  {(activeMultiFilters.fromDate ||
                    activeMultiFilters.toDate) && (
                    <span className="d-block">
                      Date:{" "}
                      {activeMultiFilters.fromDate
                        ? toApiDDMMYYYY(activeMultiFilters.fromDate)
                        : "-"}{" "}
                      to{" "}
                      {activeMultiFilters.toDate
                        ? toApiDDMMYYYY(activeMultiFilters.toDate)
                        : "-"}
                    </span>
                  )}
                </p>
              )}
              <div className="form-group mb-3">
                <label className="mb-2 d-block">Export Type</label>
                <div className="custom-control custom-radio custom-control-inline">
                  <input
                    type="radio"
                    id="exportAllQuotes"
                    name="exportTypeQuotes"
                    className="custom-control-input"
                    value="all"
                    checked={exportMode === "all"}
                    onChange={(e) => setExportMode(e.target.value)}
                    disabled={isExporting}
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="exportAllQuotes"
                  >
                    All
                  </label>
                </div>
                <div className="custom-control custom-radio custom-control-inline">
                  <input
                    type="radio"
                    id="exportDateQuotes"
                    name="exportTypeQuotes"
                    className="custom-control-input"
                    value="date"
                    checked={exportMode === "date"}
                    onChange={(e) => setExportMode(e.target.value)}
                    disabled={isExporting}
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="exportDateQuotes"
                  >
                    Filter by date
                  </label>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label className="mb-1">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={exportFromDate}
                    onChange={(e) => setExportFromDate(e.target.value)}
                    disabled={isExporting || exportMode !== "date"}
                    max={exportToDate || undefined}
                  />
                </div>
                <div className="form-group col-md-6 mb-0">
                  <label className="mb-1">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={exportToDate}
                    onChange={(e) => setExportToDate(e.target.value)}
                    disabled={isExporting || exportMode !== "date"}
                    min={exportFromDate || undefined}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowExportModal(false)}
                disabled={isExporting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-blue"
                onClick={handleExportSalesQuotes}
                disabled={isExporting}
              >
                {isExporting ? "Exporting..." : "Export"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showExportModal && <div className="modal-backdrop fade show" />}
    </div>
  );
};

export default SalesQuotes;
